import { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { AddRestaurantOrder } from "../utilities/WebCommunicator";
import { LeftOutlined, MinusOutlined, PlusOutlined, CheckCircleOutlined, UserOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import { Button } from "antd";

const customerSchema = Yup.object({
    customerName: Yup.string().required('Please enter your name').trim()
        .min(3, 'Name must be at least 3 characters').max(30, 'Name must be 30 characters or less')
        .matches(/^[A-Za-z0-9\s]+$/, 'Name should not contain special characters'),
    mobileNo: Yup.string().required('Please enter your mobile number')
        .matches(/^\d+$/, 'Mobile number must contain only digits')
        .matches(/^[1-9][0-9]{9,12}$/, 'Enter a valid 10-13 digit mobile number'),
    email: Yup.string().email('Please enter a valid email address'),
});

export const ItemDetails = ({ value, authOpts, subscriptionId, restaurantId, restaurantName, resetForm, show, onHide, onOrderSuccess }) => {
    const [updating, setUpdating] = useState(false);
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [showCustomerModal, setShowCustomerModal] = useState(false);

    const formik = useFormik({
        initialValues: {
            id: 0, isActive: true, restaurantId: restaurantId, restaurantName: restaurantName,
            serviceType: "Home Delivery", onlineOrderSourceId: 1, onlineOrderSource: "Zomato",
            customerName: "", mobileNo: "", email: "", address: "",
            tableId: null, OrderTakenById: null, noOfPax: 1, OrderStatus: 0,
            DelayTime: null, kotType: "NORMAL",
            totalQuantity: 0, totalAmount: 0,
            remarks: "Home Delivery", lineItems: [],
        },
        onSubmit: () => {},
    });

    const customerFormik = useFormik({
        initialValues: { customerName: "", mobileNo: "", email: "" },
        validationSchema: customerSchema,
        onSubmit: async (customerValues) => {
            const orderData = {
                ...formik.values,
                ...customerValues,
                restaurantId: restaurantId,
                serviceType: restaurantName ? `${restaurantName}/HOME DELIVERY` : "Home Delivery",
            };
            if (!orderData.lineItems.length) {
                toast.warning("Your cart is empty. Please add items first.");
                return;
            }
            try {
                setUpdating(true);
                const result = await AddRestaurantOrder(orderData, authOpts || { subscriptionId });
                if (result?.status === false) {
                    toast.error(result.message || "Failed to place order. Please try again.");
                } else {
                    setShowCustomerModal(false);
                    setOrderSuccess(true);
                    customerFormik.resetForm();
                }
            } catch {
                toast.error("Something went wrong while placing your order. Please try again.");
            } finally {
                setUpdating(false);
            }
        },
    });

    useEffect(() => {
        if (value?.lineItems) {
            formik.setValues(prev => ({ ...prev, ...value }));
        }
    }, [value]);

    const handleQuantityChange = useCallback((index, newQty) => {
        if (index < 0) return;
        formik.setValues(prev => {
            const lineItems = [...prev.lineItems];
            lineItems[index] = { ...lineItems[index], quantity: newQty, amount: lineItems[index].unitPrice * newQty };
            return { ...prev, lineItems };
        });
    }, []);

    const removeRow = useCallback((index) => {
        formik.setValues(prev => ({
            ...prev, lineItems: prev.lineItems.filter((_, i) => i !== index),
        }));
    }, []);

    const addRow = useCallback((item) => {
        formik.setValues(prev => {
            const lineItems = [...prev.lineItems];
            const idx = lineItems.findIndex(li => li.itemName === item?.itemName);
            if (idx > -1) {
                lineItems[idx] = {
                    ...lineItems[idx],
                    quantity: lineItems[idx].quantity + 1,
                    amount: (lineItems[idx].quantity + 1) * lineItems[idx].unitPrice,
                };
            } else {
                lineItems.push({
                    index: lineItems.length, isActive: true,
                    itemId: item?.id, itemName: item?.itemName,
                    itemUnitId: item?.itemUnitId, unitPrice: item?.unitPrice,
                    quantity: 1, tax: item?.tax || 0, amount: item?.unitPrice,
                });
            }
            return { ...prev, lineItems };
        });
    }, []);

    useEffect(() => {
        const { totalQuantity, totalAmount } = (formik.values?.lineItems || []).reduce(
            (acc, row) => ({ totalQuantity: acc.totalQuantity + row.quantity, totalAmount: acc.totalAmount + row.amount }),
            { totalQuantity: 0, totalAmount: 0 }
        );
        formik.setFieldValue("totalQuantity", totalQuantity);
        formik.setFieldValue("totalAmount", totalAmount);
    }, [formik.values.lineItems]);

    const handleClose = () => {
        value.lineItems = formik.values.lineItems;
        onHide();
    };

    const handleSuccessClose = () => {
        setOrderSuccess(false);
        if (onOrderSuccess) onOrderSuccess();
    };

    const handlePlaceOrder = () => {
        if (!formik.values.lineItems.length) {
            toast.warning("Your cart is empty.");
            return;
        }
        setShowCustomerModal(true);
    };

    const lineItems = formik.values?.lineItems?.filter(Boolean) || [];

    const inputStyle = (hasError) => ({
        width: '100%', padding: '12px 14px 12px 42px', borderRadius: 10, fontSize: 14, outline: 'none',
        border: `1.5px solid ${hasError ? '#e53935' : '#ddd'}`, background: '#f9f9f9',
        transition: 'border-color 0.2s',
    });

    const iconStyle = { position: 'absolute', left: 14, top: 14, fontSize: 16, color: '#999' };

    return (
        <>
            <Modal show={show} onHide={handleClose} fullscreen="sm-down" size="lg" centered
                style={showCustomerModal ? { filter: 'blur(4px)', pointerEvents: 'none' } : {}}>
                {/* Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #e53935, #d32f2f)', color: '#fff',
                    padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12,
                }}>
                    <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                        <LeftOutlined style={{ fontSize: 20, color: '#fff' }} />
                    </button>
                    <div style={{ flex: 1 }}>
                        <h5 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>Your Cart</h5>
                        <span style={{ fontSize: 12, opacity: 0.85 }}>
                            {lineItems.length} item{lineItems.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                </div>

                {/* Cart Items */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#f8f8f8', maxHeight: '55vh' }}>
                    {lineItems.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                            <p style={{ fontSize: 16 }}>Your cart is empty</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {lineItems.map((item, index) => {
                                const qty = item?.quantity || 0;
                                return (
                                    <div key={index} style={{
                                        background: '#fff', borderRadius: 10, padding: '12px 14px',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <p style={{ fontWeight: 600, fontSize: 14, margin: 0, color: '#222' }}>{item?.itemName}</p>
                                            <p style={{ fontSize: 13, color: '#2e7d32', fontWeight: 600, margin: '2px 0 0' }}>
                                                ₹{item?.unitPrice} × {qty} = ₹{item?.amount}
                                            </p>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                            <Button type="primary" danger shape="circle" size="small" icon={<MinusOutlined />}
                                                onClick={() => qty === 1 ? removeRow(index) : handleQuantityChange(index, qty - 1)} />
                                            <span style={{ fontWeight: 700, fontSize: 14, minWidth: 20, textAlign: 'center' }}>{qty}</span>
                                            <Button type="primary" shape="circle" size="small" icon={<PlusOutlined />}
                                                style={{ background: '#2e7d32', borderColor: '#2e7d32' }}
                                                onClick={() => addRow(item)} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Bill Summary */}
                {lineItems.length > 0 && (
                    <div style={{ padding: '12px 16px', borderTop: '1px solid #eee', background: '#fff' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ color: '#666', fontSize: 14 }}>Items ({formik.values.totalQuantity})</span>
                            <span style={{ fontWeight: 600, fontSize: 14 }}>₹{formik.values.totalAmount}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px dashed #ddd' }}>
                            <span style={{ fontWeight: 700, fontSize: 16, color: '#222' }}>Total</span>
                            <span style={{ fontWeight: 700, fontSize: 16, color: '#e53935' }}>₹{formik.values.totalAmount}</span>
                        </div>
                    </div>
                )}

                {/* Place Order Button → opens customer modal */}
                {lineItems.length > 0 && (
                    <div style={{ padding: '12px 16px 16px', background: '#fff' }}>
                        <button onClick={handlePlaceOrder} style={{
                            width: '100%', padding: '14px', borderRadius: 12, border: 'none', cursor: 'pointer',
                            background: 'linear-gradient(135deg, #e53935, #c62828)', color: '#fff',
                            fontWeight: 700, fontSize: 16, boxShadow: '0 4px 12px rgba(229,57,53,0.3)',
                        }}>
                            Proceed to Order • ₹{formik.values.totalAmount}
                        </button>
                    </div>
                )}
            </Modal>

            {/* Customer Details Modal */}
            <Modal show={showCustomerModal} onHide={() => setShowCustomerModal(false)}
                centered size="sm" backdrop="static" style={{ zIndex: 1060 }}>
                <div style={{
                    background: 'linear-gradient(135deg, #e53935, #c62828)', color: '#fff',
                    padding: '16px 20px', borderRadius: '6px 6px 0 0',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div>
                        <h5 style={{ margin: 0, fontWeight: 700, fontSize: 17 }}>Complete Your Order</h5>
                        <p style={{ margin: '2px 0 0', fontSize: 12, opacity: 0.85 }}>Please enter your details</p>
                    </div>
                    <button onClick={() => setShowCustomerModal(false)}
                        style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', padding: 4 }}>
                        ✕
                    </button>
                </div>

                <div style={{ padding: '20px 20px 24px' }}>
                    {/* Customer Name */}
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6, display: 'block' }}>
                            Full Name <span style={{ color: '#e53935' }}>*</span>
                        </label>
                        <div style={{ position: 'relative' }}>
                            <UserOutlined style={iconStyle} />
                            <input type="text" name="customerName" placeholder="Enter your full name"
                                value={customerFormik.values.customerName}
                                onChange={customerFormik.handleChange} onBlur={customerFormik.handleBlur}
                                style={inputStyle(customerFormik.touched.customerName && customerFormik.errors.customerName)} />
                        </div>
                        {customerFormik.touched.customerName && customerFormik.errors.customerName && (
                            <p style={{ color: '#e53935', fontSize: 12, margin: '4px 0 0' }}>{customerFormik.errors.customerName}</p>
                        )}
                    </div>

                    {/* Mobile Number */}
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6, display: 'block' }}>
                            Mobile Number <span style={{ color: '#e53935' }}>*</span>
                        </label>
                        <div style={{ position: 'relative' }}>
                            <PhoneOutlined style={iconStyle} />
                            <input type="tel" name="mobileNo" placeholder="Enter your mobile number"
                                value={customerFormik.values.mobileNo}
                                onChange={customerFormik.handleChange} onBlur={customerFormik.handleBlur}
                                style={inputStyle(customerFormik.touched.mobileNo && customerFormik.errors.mobileNo)} />
                        </div>
                        {customerFormik.touched.mobileNo && customerFormik.errors.mobileNo && (
                            <p style={{ color: '#e53935', fontSize: 12, margin: '4px 0 0' }}>{customerFormik.errors.mobileNo}</p>
                        )}
                    </div>

                    {/* Email */}
                    <div style={{ marginBottom: 20 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6, display: 'block' }}>
                            Email Address
                        </label>
                        <div style={{ position: 'relative' }}>
                            <MailOutlined style={iconStyle} />
                            <input type="email" name="email" placeholder="Enter your email address"
                                value={customerFormik.values.email}
                                onChange={customerFormik.handleChange} onBlur={customerFormik.handleBlur}
                                style={inputStyle(customerFormik.touched.email && customerFormik.errors.email)} />
                        </div>
                        {customerFormik.touched.email && customerFormik.errors.email && (
                            <p style={{ color: '#e53935', fontSize: 12, margin: '4px 0 0' }}>{customerFormik.errors.email}</p>
                        )}
                    </div>

                    {/* Submit Button */}
                    <button onClick={customerFormik.handleSubmit} disabled={updating}
                        style={{
                            width: '100%', padding: '13px', borderRadius: 10, border: 'none',
                            cursor: updating ? 'not-allowed' : 'pointer',
                            background: updating ? '#999' : 'linear-gradient(135deg, #e53935, #c62828)', color: '#fff',
                            fontWeight: 700, fontSize: 15, boxShadow: '0 4px 12px rgba(229,57,53,0.3)',
                            display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8,
                        }}>
                        {updating ? (
                            <><span className="spinner-border spinner-border-sm" /> Placing Order...</>
                        ) : (
                            <>Place Order • ₹{formik.values.totalAmount}</>
                        )}
                    </button>
                </div>
            </Modal>

            {/* Order Success Overlay */}
            {orderSuccess && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999,
                    display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20,
                }}>
                    <div style={{
                        background: '#fff', borderRadius: 16, padding: 32, maxWidth: 360, width: '100%',
                        textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                    }}>
                        <CheckCircleOutlined style={{ fontSize: 56, color: '#2e7d32' }} />
                        <h4 style={{ margin: '16px 0 8px', color: '#222', fontWeight: 700 }}>Order Placed!</h4>
                        <p style={{ color: '#666', fontSize: 14, margin: '0 0 20px' }}>
                            Your order has been placed successfully. It will be prepared shortly.
                        </p>
                        <button onClick={handleSuccessClose} style={{
                            padding: '10px 32px', borderRadius: 25, border: 'none', cursor: 'pointer',
                            background: '#2e7d32', color: '#fff', fontWeight: 600, fontSize: 15,
                        }}>
                            Done
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};
